import {
  Condition,
  DiagnosticReport,
  Encounter,
  Location,
  Organization,
  Practitioner,
} from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { toTitleCase } from "@metriport/shared";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getImagingReportIdsFromProcedures,
  getNoteCategory,
} from "../../../shared-components/fhir-sections/reports/note-type-utils";
import { getDiagnosisText } from "../../../shared-components/fhir-sections/reports/report-content";
import { getSpecialtyForReport } from "../../../shared-components/fhir-sections/reports/specialty-utils";
import { getReportTypeByLoinc } from "../../../shared-components/fhir-sections/reports/table-data";
import { extractNotesFromDiagnosticReport } from "../../utils/note-processing";
import { TableRow } from "../../utils/tables-metadata";
import _ from "lodash";
import { isImagingReport } from "./imaging-reports-rows";

export type DiagnosticReportRowData = TableRow & {
  id: string;
  typeOfReport: string;
  noteCategory: string;
  specialty: string;
  reasonForVisit: string;
  location: string;
  date: string;
  originalData: Smart<DiagnosticReport>;
  noteData?: {
    fullText: string;
  };
};

export function getDiagnosticReportRows({
  bundle,
}: {
  bundle: FhirBundleSdk;
}): DiagnosticReportRowData[] {
  const diagnosticReports = bundle.getDiagnosticReports();
  const procedures = bundle.getProcedures();
  const imagingReportIds = getImagingReportIdsFromProcedures(procedures);

  const rows = _(diagnosticReports)
    .reject(report => isImagingReport(report, imagingReportIds, bundle))
    .map(report => buildDiagnosticReportRow(report, bundle, imagingReportIds))
    .filter((row): row is DiagnosticReportRowData => row !== null)
    .value();

  return rows.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
}

function buildDiagnosticReportRow(
  diagnosticReport: Smart<DiagnosticReport>,
  bundle: FhirBundleSdk,
  imagingReportIds: Set<string>
): DiagnosticReportRowData | null {
  // Skip imaging reports (they have their own table)
  if (diagnosticReport.id && imagingReportIds.has(diagnosticReport.id)) {
    return null;
  }

  // Skip reports without actual note content (presentedForm)
  // NOTE: V1 only checked this for orphaned reports, but we should check for ALL reports
  // A diagnostic report without presentedForm has no clinical content to display
  if (!diagnosticReport.presentedForm) {
    return null;
  }

  // Get encounter using SDK's built-in O(1) lookup
  const encounterId = diagnosticReport.encounter?.reference?.split("/").pop();
  const encounter = encounterId ? bundle.getResourceById<Encounter>(encounterId) : undefined;

  // Use SDK to get referenced resources
  const participants = getDiagnosticReportParticipants(diagnosticReport, encounter, bundle);
  const organizations = getEncounterOrganizations(encounter, bundle);
  const locations = getEncounterLocations(encounter, bundle);
  const diagnoses = getEncounterDiagnoses(encounter, bundle);

  // Extract note text
  const notes = extractNotesFromDiagnosticReport(diagnosticReport);
  const fullNoteText = notes.join(" ");

  // Compute display fields
  const reasonForVisit = diagnoses.map(getDiagnosisText).filter(Boolean);
  const typeOfReport = getReportTypeByLoinc([diagnosticReport]);
  const specialty = getSpecialtyForReport(participants, organizations, locations);
  const noteCategory = getNoteCategory(
    [diagnosticReport],
    imagingReportIds,
    participants,
    organizations
  );

  const locationNames = locations
    .map(l => l.name)
    .filter(Boolean)
    .join(", ");
  const orgNames = organizations
    .map(o => o.name)
    .filter(Boolean)
    .join(", ");

  // Create synthetic encounter for orphaned reports
  const effectiveEncounter = encounter ?? createSyntheticEncounter(diagnosticReport);

  return {
    id: `${effectiveEncounter.id}-${diagnosticReport.id}`,
    typeOfReport,
    noteCategory,
    specialty,
    reasonForVisit: reasonForVisit.length ? toTitleCase(reasonForVisit.join(", ")) : "-",
    location: locationNames || orgNames || "-",
    date: effectiveEncounter.period?.start
      ? dayjs(effectiveEncounter.period.start).format(ISO_DATE)
      : "-",
    originalData: diagnosticReport,
    noteData: fullNoteText ? { fullText: fullNoteText } : undefined,
  };
}

function getDiagnosticReportParticipants(
  diagnosticReport: Smart<DiagnosticReport>,
  encounter: Encounter | undefined,
  bundle: FhirBundleSdk
): Practitioner[] {
  // Try DiagnosticReport performers first
  const performerIds =
    diagnosticReport.performer
      ?.filter(p => p.reference?.startsWith("Practitioner/"))
      .map(p => p.reference?.split("/").pop())
      .filter((id): id is string => !!id) ?? [];

  const reportPractitioners = performerIds
    .map(id => bundle.getResourceById<Practitioner>(id))
    .filter(p => p !== undefined) as Practitioner[];

  if (reportPractitioners.length > 0) {
    return reportPractitioners;
  }

  // Fall back to encounter participants
  return getEncounterParticipants(encounter, bundle);
}

function getEncounterParticipants(
  encounter: Encounter | undefined,
  bundle: FhirBundleSdk
): Practitioner[] {
  if (!encounter) return [];

  const participantIds =
    encounter.participant
      ?.map(p => p.individual?.reference?.split("/").pop())
      .filter((id): id is string => !!id) ?? [];

  return participantIds
    .map(id => bundle.getResourceById<Practitioner>(id))
    .filter(p => p !== undefined) as Practitioner[];
}

function getEncounterOrganizations(
  encounter: Encounter | undefined,
  bundle: FhirBundleSdk
): Organization[] {
  if (!encounter) return [];

  const orgId = encounter.serviceProvider?.reference?.split("/").pop();
  if (!orgId) return [];

  const org = bundle.getResourceById<Organization>(orgId);
  return org ? [org] : [];
}

function getEncounterLocations(
  encounter: Encounter | undefined,
  bundle: FhirBundleSdk
): Location[] {
  if (!encounter) return [];

  const locationIds =
    encounter.location
      ?.map(l => l.location?.reference?.split("/").pop())
      .filter((id): id is string => !!id) ?? [];

  return locationIds
    .map(id => bundle.getResourceById<Location>(id))
    .filter(l => l !== undefined) as Location[];
}

function getEncounterDiagnoses(
  encounter: Encounter | undefined,
  bundle: FhirBundleSdk
): Condition[] {
  if (!encounter) return [];

  const diagnosisIds =
    encounter.diagnosis
      ?.map(d => d.condition?.reference?.split("/").pop())
      .filter((id): id is string => !!id) ?? [];

  return diagnosisIds
    .map(id => bundle.getResourceById<Condition>(id))
    .filter(c => c !== undefined) as Condition[];
}

function createSyntheticEncounter(diagnosticReport: DiagnosticReport): Encounter {
  return {
    resourceType: "Encounter",
    id: diagnosticReport.id,
    period: diagnosticReport.effectivePeriod ?? { start: diagnosticReport.effectiveDateTime },
  };
}
